import { validateGameDefinition, type GameDefinition } from '@archon3d/core';
import type { BoardStorage } from './boardStorage';

export const GAME_LIBRARY_KEY = 'rpg-arena.games.v1';

export interface BrowserGames {
  games: GameDefinition[];
  /** Retained verbatim as JSON values on subsequent saves, never silently discarded. */
  rejected: unknown[];
  raw: string | null;
  blocked: boolean;
  notice: string | null;
}

export function readBrowserGames(storage: Pick<BoardStorage, 'getItem'>): BrowserGames {
  const result: BrowserGames = { games: [], rejected: [], raw: null, blocked: false, notice: null };
  try {
    result.raw = storage.getItem(GAME_LIBRARY_KEY);
    const entries: unknown = JSON.parse(result.raw ?? '[]');
    if (!Array.isArray(entries)) throw new Error('Expected a game list');
    const seen = new Set<string>();
    for (const entry of entries) {
      try {
        const game = validateGameDefinition(entry);
        if (seen.has(game.id)) throw new Error('Duplicate game');
        seen.add(game.id);
        result.games.push(game);
      } catch {
        result.rejected.push(entry);
      }
    }
    if (result.rejected.length)
      result.notice = `${result.rejected.length} browser ${result.rejected.length === 1 ? 'game' : 'games'} could not be loaded. Other games are available. Unreadable entries are preserved in your backup.`;
  } catch {
    result.blocked = true;
    result.notice =
      'The browser game library could not be read. It has not been changed. Browser-library saves and deletion are blocked to protect it; export any new work from the editor.';
  }
  return result;
}

/** Read again before writing, retaining invalid entries and other tabs’ latest saved games. */
export function updateBrowserGames(
  storage: BoardStorage,
  update: (games: GameDefinition[]) => GameDefinition[],
): BrowserGames {
  const current = readBrowserGames(storage);
  if (current.blocked) throw new Error(current.notice!);
  const games = update(current.games);
  const raw = JSON.stringify([...games, ...current.rejected]);
  storage.setItem(GAME_LIBRARY_KEY, raw); // A failed write must never be reported as saved.
  return { ...current, games, raw };
}
